import { useGlobalContext } from '@/components/Context/hooks/useGlobalContext';
import { Hit, type OBJECT_TYPE } from '@/components/SearchOverlay/Hit';
import { HITS_PER_PAGE } from '@/config/config';
import { TrackingService } from '@/service/tracking/trackingService';
import type { ImageType } from '@/types/ICardData';
import { useHits } from 'react-instantsearch';

export const SuggestionsIndex = ({
  searchData,
  objectType,
  searchValue,
  onClick,
  onResultsUpdate,
  indexName,
  numberOfAlreadyViewedHitsBeforeThisIndex,
}: {
  searchData: {
    copy?: {
      searchCategoryTips: string;
      searchCategoryProducts: string;
      searchCategoryRecipes: string;
      searchCategoryArticles: string;
    };
  };
  searchValue?: string;
  onClick?: () => void;
  onResultsUpdate?: (numberOfHits: number | null) => void;
  objectType: keyof typeof OBJECT_TYPE;
  indexName: string;
  numberOfAlreadyViewedHitsBeforeThisIndex: number;
}) => {
  const { userToken } = useGlobalContext();
  const { results } = useHits();

  if (!results?.__isArtificial && typeof onResultsUpdate === 'function') {
    const resultsHits = results?.query ? results?.nbHits : null;
    onResultsUpdate(resultsHits);
  }

  if (!results?.query) {
    return null;
  }

  return results?.hits?.map((hit, index) => {
    const numberOfVisibleHitsBeforeThisIndex =
      numberOfAlreadyViewedHitsBeforeThisIndex > HITS_PER_PAGE
        ? HITS_PER_PAGE
        : numberOfAlreadyViewedHitsBeforeThisIndex;
    const position = index + (numberOfVisibleHitsBeforeThisIndex ?? 0) + 1; // +1 because algolia starts counting from 1

    return (
      <Hit
        hit={{
          id: hit.objectID,
          category: hit.category as string,
          title: hit.title as string,
          type: hit.type as string,
          slug: hit.slug as string,
          images: hit.images as unknown as ImageType[],
          objectType,
        }}
        searchValue={searchValue}
        key={hit.objectID}
        copy={searchData?.copy}
        onClick={() => {
          TrackingService.clickSuggestion(
            {
              category: objectType || '',
              subCategory: hit.category || '',
              searchSuggestion: hit.title || '',
            },
            position,
            searchValue,
            results?.hits.length,
          );
          if (onClick && typeof onClick === 'function') {
            onClick();

            if (hit.objectID) {
              TrackingService.clickedObjectIDsAfterSearch({
                indexName,
                objectIDs: [hit.objectID],
                positions: [position],
                queryID: results?.queryID as string,
                slug: hit.slug as string,
                objectType,
                userToken,
              });
            }
          }
        }}
      />
    );
  });
};
